import type { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { ReactElement } from "react";
import {
  DataTable,
  DataTableContainer,
  useDataTableController,
} from "@/shared/components/data-table";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import { SampleSetNumber } from "@/shared/components/domain/sample-set/sample-set-number";
import {
  Incubation,
  Procedure,
} from "@/services/backend/procedures/procedure/procedure";
import { sampleTypes } from "@/shared/components/enum-labels";
import { H4 } from "@/shared/components/typography";
import { useListProceduresQuery } from "@/services/backend/procedures/procedure/service";
import MouldResult from "@/shared/components/domain/moulds/mould-result";
import { CollapsibleDataTable } from "@/shared/components/collapsible-data-table";
import { ProcedureType, SampleType } from "@/services/backend/types";
import { MouldResultTable } from "@/shared/components/domain/differentiation/mould-result-table";
import MeasurementLabelComponent from "./measurment-label-components";
import ResultLabel from "./result-label";

function getMeasurementUnit(
  procedureType: ProcedureType,
  sequenceNumber: number,
): string {
  switch (procedureType) {
    case ProcedureType.RLT2N:
      if (sequenceNumber === 1) {
        return "KBE/Platte";
      }
      return "KBE/Platte";
    case ProcedureType.RLT1N:
      if (sequenceNumber === 1) {
        return "KBE/Platte";
      }
      return "KBE/Platte";
    default:
      return "";
  }
}

function getResultUnit(sampleType: SampleType): string {
  switch (sampleType) {
    case SampleType.AirMicrobial:
      return "KBE/m3";
    case SampleType.SurfaceMicrobial:
      return `25/cm2`;
    default:
      return "";
  }
}

const createTableColumns = (
  procedureType: ProcedureType,
  sampleType: SampleType,
): ColumnDef<Procedure>[] => {
  const columns: ColumnDef<Procedure>[] = [
    {
      id: "sampleSet",
      accessorKey: "procedure",
      header: "Probenset",
      cell: ({ row }) => {
        const procedureData = row.original;
        return <SampleSetNumber sampleSetId={procedureData.sampleSetId} />;
      },
    },
    {
      id: "sampleType",
      accessorKey: "sampleType",
      header: "Probentyp",
      cell: ({ row }) => {
        const procedureData = row.original;
        return <div>{sampleTypes[procedureData.sampleType]}</div>;
      },
    },
    {
      id: "measurement-1",
      accessorKey: "incubation",
      header: `1. Auszählung (${getMeasurementUnit(procedureType, 1)})`,
      cell: ({ row }) => {
        const procedureData = row.original;
        const incubation = procedureData.incubation.find(
          (value: Incubation) => value.sequenceNumber === 1,
        );
        return incubation != null ? (
          <MeasurementLabelComponent
            incubation={incubation}
            sampleSetId={procedureData.sampleSetId}
          />
        ) : (
          "Keine Probe gefunden"
        );
      },
    },
    {
      id: "corrected-measurement-1",
      accessorKey: "incubation",
      header: `1. Korrekturwert (${getMeasurementUnit(procedureType, 1)})`,
      cell: ({ row }) => {
        const procedureData = row.original;
        const incubation = procedureData.incubation.find(
          (value: Incubation) => value.sequenceNumber === 1,
        );
        return incubation != null ? (
          <MeasurementLabelComponent
            incubation={incubation}
            sampleSetId={procedureData.sampleSetId}
            correctedValue
          />
        ) : (
          "Keine Probe gefunden"
        );
      },
    },
    {
      id: "measurement-2",
      accessorKey: "incubation",
      header: `2. Auszählung (${getMeasurementUnit(procedureType, 2)})`,
      cell: ({ row }) => {
        const procedureData = row.original;
        const incubation = procedureData.incubation.find(
          (value: Incubation) => value.sequenceNumber === 2,
        );
        return incubation != null ? (
          <MeasurementLabelComponent
            incubation={incubation}
            sampleSetId={procedureData.sampleSetId}
          />
        ) : (
          "Keine Probe gefunden"
        );
      },
    },
    {
      id: "corrected-measurement-2",
      accessorKey: "incubation",
      header: `2. Korrekturwert (${getMeasurementUnit(procedureType, 2)})`,
      cell: ({ row }) => {
        const procedureData = row.original;
        const incubation = procedureData.incubation.find(
          (value: Incubation) => value.sequenceNumber === 2,
        );
        return incubation != null ? (
          <MeasurementLabelComponent
            incubation={incubation}
            correctedValue
            sampleSetId={procedureData.sampleSetId}
          />
        ) : (
          "Keine Probe gefunden"
        );
      },
    },
    {
      id: "results",
      accessorKey: "results",
      header: `Ergebnisse (${getResultUnit(sampleType)})`,
      cell: ({ row }) => {
        const procedureData = row.original;
        return <ResultLabel procedure={procedureData} />;
      },
    },
  ];

  if (sampleType === SampleType.AirMicrobial) {
    columns.push({
      id: "mouldResult",
      accessorKey: "id",
      header: "Schimmelpilze",
      cell: ({ row }) => {
        const procedureData = row.original;
        if (procedureData.sampleType === "AirMicrobial") {
          return (
            <div className="flex items-center space-x-2">
              <MouldResult procedure={procedureData} />
            </div>
          );
        }
        return <div>Keine Differenzierung</div>;
      },
    });
  }
  return columns;
};

interface CalculationTableProps {
  reportId: string;
  procedureType: ProcedureType;
  sampleType: SampleType;
  label: string;
  visibilityState?: VisibilityState;
  collapsible?: boolean;
}

function ResultTable({
  reportId,
  procedureType,
  sampleType,
  label,
  visibilityState = {},
  collapsible = false,
}: CalculationTableProps): ReactElement {
  const {
    data: list,
    error,
    isLoading,
    isFetching,
  } = useListProceduresQuery(
    {
      ownerId: {
        active: true,
        values: [reportId],
      },
      ownerType: {
        active: true,
        values: ["inspections.Inspection"],
      },
      procedureType: {
        active: true,
        values: [procedureType],
      },
      sampleType: {
        active: true,
        values: [sampleType],
      },
      limit: 0,
      offset: 0,
    },
    { refetchOnMountOrArgChange: true },
  );

  const cols = createTableColumns(procedureType, sampleType);
  const table = useDataTableController(
    cols,
    list?.items || [],
    visibilityState,
  );

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading || isFetching) {
    return (
      <DataTableContainer className="w-full">
        <DataTable table={table} columns={cols} isLoading />
      </DataTableContainer>
    );
  }

  if (list != null && list.items != null && list.items.length > 0) {
    return (
      <div className="flex w-full flex-col space-y-4">
        <H4>{`${label} - ${sampleTypes[sampleType]}`}</H4>
        <DataTableContainer className="w-full">
          {sampleType === SampleType.AirMicrobial ? (
            <CollapsibleDataTable
              table={table}
              columns={cols}
              renderCollapsibleContent={(procedure) => (
                <MouldResultTable procedure={procedure} />
              )}
              collapsibleTriggerId={collapsible ? "sampleSet" : ""}
            />
          ) : (
            <DataTable table={table} columns={cols} />
          )}
        </DataTableContainer>
      </div>
    );
  }

  return <div />;
}

export default ResultTable;
